import { Lightning } from '@lightningjs/sdk';
import _merge from 'lodash/merge';
import { defaultContext, getInitialStyle } from './keyboard.style';
import { KeyboardItem } from './keyItem/keyItem.component';
import {
  KEY_WIDTH,
  KEY_HEIGHT,
  KEYS_GAP,
  getBasicIconViewBox,
  buttonColor,
  getBasicLetter,
} from './keyItem/keyItem.style';
import { DEFAULT_KEYBOARD, SPECIAL_KEYS, ITEMS_PER_ROW, LETTER_SELECTED_SIGNAL } from './keyboard.constants';
import Icon from '../icon/icon.component';
import { SubKeyboard } from './subKeyboard/subKeyboard.component';
import { CLOSE_SUB_KEYBOARD_SIGNAL } from './subKeyboard/subKeyboard.constants';

export default class Keyboard extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {},
        Keys: {},
        SubKeys: {
          type: SubKeyboard,
          signals: {
            [CLOSE_SUB_KEYBOARD_SIGNAL]: '_handleCloseSubKeyboard',
          },
          passSignals: {
            [LETTER_SELECTED_SIGNAL]: true,
          },
        },
      },
      getInitialStyle(),
    );
  }

  _init() {
    this._index = 0;
    this._style = defaultContext;
    this._keyBoardLayout = [0];
    this.numberOfSpecialButtons = 0;
    this.initialize = DEFAULT_KEYBOARD;
  }

  set initialize(letterRow) {
    let numberOfButtons = 0;

    this.tag('Keys').children = letterRow.map((symbol, index) => {
      if (Object.keys(SPECIAL_KEYS).includes(symbol)) {
        const { keyLength, icon } = SPECIAL_KEYS[symbol];
        for (let i = 0; i < keyLength; i += 1) {
          this._keyBoardLayout[numberOfButtons] = index;
          numberOfButtons += 1;
        }
        this.numberOfSpecialButtons += keyLength - 1;

        const width = KEY_WIDTH[this._style.screenSize] + KEYS_GAP;
        const height = KEY_HEIGHT[this._style.screenSize] + KEYS_GAP;
        const iconViewBox = getBasicIconViewBox(this._style.screenSize);

        return {
          type: KeyboardItem,
          x: Math.round(width * ((numberOfButtons - keyLength) % ITEMS_PER_ROW)),
          y: Math.round(height * Math.floor((numberOfButtons - keyLength) / ITEMS_PER_ROW)),
          Button: {
            color: buttonColor.iconBackgroundUnFocused[this._style.colorMode],
            w: keyLength * KEY_WIDTH[this._style.screenSize] + KEYS_GAP,
          },
          Icon: {
            type: Icon,
            x: Math.round((keyLength * width - iconViewBox.w) / 2),
            y: Math.round((height - iconViewBox.h) / 2),
            iconData: {
              picto: icon,
              viewBox: iconViewBox,
            },
          },
        };
      }

      this._keyBoardLayout[numberOfButtons] = index;
      numberOfButtons += 1;

      const isMultiple = Array.isArray(symbol);
      const viewSymbol = isMultiple ? symbol[0] : symbol;

      return {
        type: KeyboardItem,
        x: (KEY_WIDTH[this._style.screenSize] + KEYS_GAP) * ((numberOfButtons - 1) % ITEMS_PER_ROW),
        y: (KEY_HEIGHT[this._style.screenSize] + KEYS_GAP) * Math.floor((numberOfButtons - 1) / ITEMS_PER_ROW),
        Button: {
          SpecialIndicator: {
            visible: isMultiple,
          },
        },
        ...getBasicLetter({ symbol: viewSymbol, ...this._style }),
      };
    });

    this._setIndex();
  }

  _handleCloseSubKeyboard() {
    this._focused = this.tag('Keys').children[this._index];
  }

  _handleUp() {
    const isBelowFirstRow = this._index + this.numberOfSpecialButtons >= ITEMS_PER_ROW;
    if (isBelowFirstRow) {
      this._setIndex(this._keyBoardLayout[this._keyBoardLayout.indexOf(this._index) - ITEMS_PER_ROW]);
    }

    return false;
  }

  _handleDown() {
    const lastRowStartIndex = Math.ceil(this._keyBoardLayout.length / ITEMS_PER_ROW - 1) * ITEMS_PER_ROW;

    const hasNoItemBellow = this._index + ITEMS_PER_ROW >= this._keyBoardLayout.length;

    const isLastRow = this._keyBoardLayout.indexOf(this._index) >= lastRowStartIndex;

    if (isLastRow || hasNoItemBellow) return false;

    this._setIndex(this._keyBoardLayout[this._keyBoardLayout.indexOf(this._index) + ITEMS_PER_ROW]);

    return true;
  }

  _handleLeft() {
    const isBeginningOfRow = this._keyBoardLayout.indexOf(this._index) % ITEMS_PER_ROW === 0;
    if (isBeginningOfRow) {
      return false;
    }
    if (this._index > 0) {
      this._setIndex(this._index - 1);

      return true;
    }

    return false;
  }

  _handleRight() {
    const isEndOfRow = this._keyBoardLayout.lastIndexOf(this._index) % ITEMS_PER_ROW >= ITEMS_PER_ROW - 1;
    if (!isEndOfRow) {
      this._setIndex(this._index + 1);

      return true;
    }

    return false;
  }

  _handleEnter() {
    const selectedItem = DEFAULT_KEYBOARD[this._index];
    if (!Array.isArray(selectedItem)) {
      this.signal(LETTER_SELECTED_SIGNAL, selectedItem);

      return;
    }

    const { x, y } = this._focused;
    this.tag('SubKeys').initialize = { x, y, items: selectedItem };
    this._focused = this.tag('SubKeys');
  }

  _setIndex(index = this._index) {
    this._index = index;
    this._focused = this.tag('Keys').children[this._index];
  }

  _getFocused() {
    return this._focused;
  }
}
